/* =========================================================
   fj-cart.js
   Cart line updates via the Ajax Cart API + Section Rendering API.

   Defines:
   - <fj-cart>       wraps any region that renders cart lines + summary
   - <fj-cart-count> the header bubble that shows the item count

   Emits:
   - fj:cart:updated  { itemCount, cart }  (bubbles)
   ========================================================= */

(() => {
  const CART_UPDATED_EVENT = 'fj:cart:updated';

  class FjCart extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
    }

    handleClick(event) {
      const control = event.target.closest(
        '[data-quantity-decrease], [data-quantity-increase], [data-fj-cart-remove]'
      );
      if (!control) return;

      const line = control.closest('[data-fj-cart-line]');
      if (!line) return;

      event.preventDefault();

      if (control.hasAttribute('data-fj-cart-remove')) {
        this.updateLine(line, 0);
        return;
      }

      const input = line.querySelector('[data-quantity-input]');
      const step = control.hasAttribute('data-quantity-increase') ? 1 : -1;
      const next = Math.max(1, (Number(input.value) || 1) + step);

      this.updateLine(line, next);
    }

    handleChange(event) {
      const input = event.target.closest('[data-quantity-input]');
      if (!input) return;

      const line = input.closest('[data-fj-cart-line]');
      if (!line) return;

      const quantity = Math.max(1, Number(input.value) || 1);

      if (quantity === Number(input.defaultValue)) {
        input.value = quantity;
        return;
      }

      this.updateLine(line, quantity);
    }

    async updateLine(line, quantity) {
      if (this.busy) return;

      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      line.classList.add('fj-cart-item--busy');
      this.setError('');

      try {
        const response = await fetch(this.getAttribute('data-fj-cart-change-url'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: line.getAttribute('data-fj-cart-line'),
            quantity,
            sections: FjCart.sectionIds(),
            sections_url: window.location.pathname
          })
        });

        const cart = await response.json();

        if (!response.ok) {
          throw new Error(
            cart.errors || cart.description || cart.message || 'Cart update failed.'
          );
        }

        FjCart.renderSections(cart.sections);

        this.dispatchEvent(
          new CustomEvent(CART_UPDATED_EVENT, {
            bubbles: true,
            detail: { itemCount: cart.item_count, cart }
          })
        );
      } catch (error) {
        line.classList.remove('fj-cart-item--busy');
        this.setError(error.message);
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
      }
    }

    setError(message) {
      const target = this.querySelector('[data-fj-cart-errors]');
      if (!target) return;
      target.textContent = message;
      target.toggleAttribute('hidden', message === '');
    }

    /* Every mounted <fj-cart> asks the server to re-render its own section,
       so the page cart and the drawer stay in sync from a single request. */
    static sectionIds() {
      return Array.from(document.querySelectorAll('fj-cart[data-fj-section-id]'))
        .map((element) => element.getAttribute('data-fj-section-id'))
        .join(',');
    }

    static renderSections(sections) {
      if (!sections) return;

      const parser = new DOMParser();

      Object.keys(sections).forEach((id) => {
        const target = document.querySelector(
          `fj-cart[data-fj-section-id="${id}"] [data-fj-cart-contents]`
        );
        if (!target) return;

        const fresh = parser
          .parseFromString(sections[id], 'text/html')
          .querySelector('[data-fj-cart-contents]');

        if (fresh) target.innerHTML = fresh.innerHTML;
      });
    }
  }

  class FjCartCount extends HTMLElement {
    constructor() {
      super();
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
    }

    connectedCallback() {
      document.addEventListener(CART_UPDATED_EVENT, this.handleCartUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener(CART_UPDATED_EVENT, this.handleCartUpdate);
    }

    handleCartUpdate(event) {
      const count = event.detail.itemCount;
      this.textContent = count;
      this.parentElement.toggleAttribute('hidden', count === 0);
    }
  }

  if (!customElements.get('fj-cart')) customElements.define('fj-cart', FjCart);
  if (!customElements.get('fj-cart-count')) customElements.define('fj-cart-count', FjCartCount);
})();
